/* Gift Offer Transformer - Final Full Version
 *
 * Responsibilities:
 *  1. Detect BOGOS.io gift blocks in the DOM.
 *  2. Extract product handle from the gift link.
 *  3. Fetch metafields JSON from /products/{handle}?view=gift-offer.
 *  4. Build a custom card layout based on client Figma design.
 *  5. Mirror countdown timer values from the original DOM.
 *  6. Use MutationObserver + fallback scan to detect offers.
 *  7. Prevent duplicates with a data-bo-processed attribute.
 *  8. Hide the original offer block after building the custom card.
 */

(function () {
  'use strict';

  // ------------------ Configuration ------------------

  const SEL_WIDGET = '#sca-gift-thumbnail'; // Widget to show once we're done
  const SEL_OFFER = '.fg-gift-thumbnail-offer'; // Gift wrapper injected by BOGOS
  const SEL_TIMEBOX = '.fg-gift-thumbnail-offer-time-detail'; // Countdown container
  const SEL_GIFT_LINK = 'a.gift-original-url'; // Link to gift product
  const SEL_FALLBACK_IMG = '.product-item-thumbnail.fg-gift-image'; // Backup image
  const ATTR_DONE = 'data-bo-processed'; // Attribute flag to avoid duplicates

  const cache = new Map(); // Cache JSON by handle

  // ------------------ Utility Functions ------------------

  function escapeHtml(s) {
    return String(s || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function normalizeSrc(src) {
    if (!src) return '';
    return src.startsWith('//') ? 'https:' + src : src;
  }

  function extractHandle(url) {
    try {
      const u = new URL(url, location.origin);
      const parts = u.pathname.split('/products/');
      if (parts.length < 2) return null;
      return parts[1].split('/')[0];
    } catch {
      return null;
    }
  }

  function linesToBullets(s) {
    if (!s) return [];
    return s
      .split(/\r?\n|•|-/g)
      .map((v) => v.trim())
      .filter(Boolean);
  }

  // ------------------ Data Fetching ------------------

  async function fetchGift(handle) {
    if (!handle) return null;
    if (cache.has(handle)) return cache.get(handle);

    const endpoint = `/products/${handle}?view=gift-offer`;
    try {
      const res = await fetch(endpoint, { credentials: 'same-origin' });
      if (!res.ok) return null;

      const txt = await res.text();
      const data = JSON.parse(txt.trim());

      cache.set(handle, data);
      return data;
    } catch (err) {
      console.error('❌ [fetchGift] Error fetching JSON for', handle, err);
      return null;
    }
  }

  // ------------------ Build Custom Card ------------------

  function buildCard(product, timeEl, fallbackImg, handle) {
    const productTitle = document.querySelector('h1.product-title')?.textContent.trim() || '';;
    const g = product?.gift || {};
    const imgSrc = normalizeSrc(g.image || fallbackImg || '');
    const title = product?.title || 'Gift';
    const ctrl = g.controlName || productTitle;
    const subtitle = `Included with the ${ctrl}.<br/>No code required, automatically added to your cart.`;
    const desc = g.description || '';
    const bullets = linesToBullets(desc);
    const productURL = `/products/${handle}`;

    const wrap = document.createElement('div');
    wrap.className = 'bo-offer-wrapper';
    wrap.innerHTML = [
    // 🔹 Mobile-only top bar
    '  <div class="bo-offer-top bo-offer-top--mobile">',
    '    <span class="bo-badge">✔ Included</span>',
    '    <span class="bo-timer">Offer Ends: <span class="bo-timer-value"></span><span class="bo-timer-spacer"></span></span>',
    '  </div>',
    '<div class="bo-offer-card">',

    `  <div class="bo-offer-media"><a href="${productURL}"><img src="${imgSrc}" alt=""></a></div>`,

    '  <div class="bo-offer-main">',
    // 🔹 Desktop-only top bar
    '    <div class="bo-offer-top bo-offer-top--desktop">',
    '      <span class="bo-badge">✔ Included</span>',
    '      <span class="bo-timer">Offer Ends: <span class="bo-timer-value"></span><span class="bo-timer-spacer"></span></span>',
    '    </div>',

    `    <div class="bo-title"><a href="${productURL}">${escapeHtml(title)}</a></div>`,
    `    <div class="bo-subtitle">${subtitle}</div>`,
    bullets.length
        ? `    <ul class="bo-bullets">${bullets.map((b) => `<li>${escapeHtml(b)}</li>`).join('')}</ul>`
        : '',
    '  </div>',
    '</div>',
    ].join('\n');


    // Mirror countdown values from the original DOM
    const timerVals = wrap.querySelectorAll('.bo-timer-value');
    const timerEls = wrap.querySelectorAll('.bo-timer');
    if (timeEl && timerVals) {
      const update = () => {
        // Grab values as numbers
        const dVal = parseInt(timeEl.querySelector('.day')?.textContent?.trim() || "0", 10);
        const hVal = parseInt(timeEl.querySelector('.hour')?.textContent?.trim() || "0", 10);
        const mVal = parseInt(timeEl.querySelector('.minutes')?.textContent?.trim() || "0", 10);
        const sVal = parseInt(timeEl.querySelector('.second')?.textContent?.trim() || "0", 10);

        // Build labels with singular/plural
        const d = `<span>${dVal} ${dVal === 1 ? "Day" : "Days"}</span>`;
        const h = `<span>${hVal} ${hVal === 1 ? "Hour" : "Hours"}</span>`;
        const m = `<span>${mVal} ${mVal === 1 ? "Min" : "Mins"}</span>`;
        const s = `<span>${sVal} ${sVal === 1 ? "Sec" : "Secs"}</span>`;

        // Only update if all values exist
        if (!isNaN(dVal) && !isNaN(hVal) && !isNaN(mVal) && !isNaN(sVal)) {
          timerVals.forEach(val => val.innerHTML = `${d} ${h} ${m} ${s}`);
          // If all values are zero, hide the timer
          if (dVal === 0 && hVal === 0 && mVal === 0 && sVal === 0) {
            timerEls.forEach(el => el.classList.add("hidden"));
          } else {
            timerEls.forEach(el => el.classList.remove("hidden"));
          }
        }
      };

      update();
      const iv = setInterval(update, 250);

      const ro = new MutationObserver(() => {
        if (!document.body.contains(wrap)) {
          clearInterval(iv);
          ro.disconnect();
        }
      });
      ro.observe(document.body, { childList: true, subtree: true });
    } else {
      wrap.querySelector('.bo-timer').classList.add("hidden");
    }
    return wrap;
  }

  // ------------------ Process One Offer ------------------

  async function process(offerEl) {
    if (!offerEl) return;

    // ✅ Prevent duplicates: guard at top
    if (offerEl.getAttribute(ATTR_DONE) === '1') return;
    offerEl.setAttribute(ATTR_DONE, '1'); // mark immediately

    const link = offerEl.querySelector(SEL_GIFT_LINK)?.getAttribute('href') || '';
    const handle = extractHandle(link);
    if (!handle) return;

    const timeEl = offerEl.querySelector(SEL_TIMEBOX) || null;
    const fallbackImg = normalizeSrc(
      offerEl.querySelector(SEL_FALLBACK_IMG)?.getAttribute('src') || ''
    );

    const product = await fetchGift(handle);
    if (!product) return;

    const card = buildCard(product, timeEl, fallbackImg, handle);
    const host = offerEl.parentElement;
    if (!host) return;

    host.insertBefore(card, offerEl.nextSibling);

    // ✅ Hide original but keep it in DOM (needed for timer sync)
    offerEl.classList.add('bo-ghost');

    // Show the widget once we're done:
    document.querySelector(SEL_WIDGET).style.display = 'block';
  }

  // ------------------ Scan & Observe ------------------

  function scan(root) {
    const scope = root && root.querySelectorAll ? root : document;
    const nodes = scope.querySelectorAll(SEL_OFFER);
    nodes.forEach((el) => process(el));
  }

  function ensureScan(retries = 10) {
    const nodes = document.querySelectorAll(SEL_OFFER);
    if (nodes.length > 0) {
      nodes.forEach((el) => process(el));
    } else if (retries > 0) {
      setTimeout(() => ensureScan(retries - 1), 1000);
    }
  }

  function startObserver() {
    const obs = new MutationObserver((muts) => {
      muts.forEach((m) => {
        m.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            if (node.matches && node.matches(SEL_OFFER)) {
              process(node);
            } else {
              scan(node);
            }
          }
        });
      });
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }

  // ------------------ Init ------------------

  function init() {
    scan(document);   // First scan
    ensureScan();     // Retry loop
    startObserver();  // Live monitoring
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
